import { useEffect, useMemo, useRef, useState } from 'react';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import type { PetitionDetailsPageUserData } from '#src/app/pages/petitionDetails/pageContext';
import { treatExperiment } from '#src/app/shared/api/experiments';
import { useTrackExperimentExposure } from '#src/app/shared/hooks/tracking';

import { useLastNameInitialExperiment } from '../useLastNameInitialExperiment';

type LastNameInitialExperiment = PetitionDetailsPageUserData['experiments']['signFormLastNameInitialExperiment'];

/**
 * Passed to `useTrackExperimentExposure` (which must be called unconditionally, per Rules of Hooks) while
 * the carousel isn't rendered — `treated: false` keeps it a no-op.
 */
const INERT_EXPERIMENT = {
	type: 'experiment-service',
	experimentName: 'sign_form_last_name_initial_experiment',
	variation: 'control',
	config: {},
	treated: false,
} as LastNameInitialExperiment;

/**
 * Treats `sign_form_last_name_initial_experiment` and fires `$exposure` the moment the recently signed
 * carousel is actually rendered.
 *
 * Treatment is deferred here rather than server-side because the analysis is scoped to sessions that saw
 * the carousel. The GraphQL field is fetched with `treat: false` and the normalizer forces
 * `treated: false`, so **mounting `<TrackExperimentExposure>` alone would fire nothing** —
 * `useTrackExperimentExposure` returns early on `type === 'experiment-service' && !treated`. The treat call
 * below is what makes any arm reportable.
 *
 * @param qualified whether the carousel actually rendered. False keeps this inert: no treat call, no
 * exposure. The caller owns this check because it needs the feed's render conditions (signer count and the
 * signature-count experiment's `suppressTicker`), which this hook has no business knowing.
 */
export function useTreatLastNameInitialExperiment(qualified: boolean): void {
	const utilityContext = useUtilityContext();
	const { experiment, isRealAssignment, petitionId } = useLastNameInitialExperiment();
	// Holds *which* petition was treated, not a boolean. A boolean would stay true across an SPA
	// petition-to-petition navigation, so the new petition's `$exposure` would fire immediately off the
	// previous petition's state — before, and regardless of, its own treat call resolving.
	const [treatedPetitionId, setTreatedPetitionId] = useState<string | null>(null);
	// Ref, not state: a second synchronous pass in the same tick would still read state's pre-update value.
	// Keyed on petition for the same reason as above — SPA nav reuses this component without unmounting.
	const treatRequestedForRef = useRef<string | null>(null);

	const resolvedExperiment = useMemo<LastNameInitialExperiment>(() => {
		if (!qualified) return INERT_EXPERIMENT;
		// `experiment.treated` covers a session that arrived treated — unreachable while the normalizer
		// forces it false, but the exposure path shouldn't depend on that. `override` is needed because
		// useTrackExperimentExposure short-circuits on `!treated` before reaching its `override` branch, so
		// QA would never get the "$exposure SUPPRESSED by override" log. No real exposure leaks: that branch
		// logs and returns before trackExposureOnce.
		const treated = treatedPetitionId === petitionId || experiment.treated || Boolean(experiment.override);
		return treated ? { ...experiment, treated: true } : experiment;
	}, [qualified, experiment, treatedPetitionId, petitionId]);

	// `resetKeys` is required, not optional: variations are deterministic per user, so without the petition
	// id an SPA petition-to-petition navigation would dedup the new petition's legitimate exposure.
	const trackExperimentExposure = useTrackExperimentExposure(resolvedExperiment, { resetKeys: [petitionId] });

	useEffect(() => {
		if (!qualified || !isRealAssignment || treatRequestedForRef.current === petitionId) return;
		treatRequestedForRef.current = petitionId;
		void (async () => {
			try {
				// treatExperiment resolves `false` rather than throwing when the treatment was not registered
				// server-side — a bot session, or an assignment that isn't treatable yet. Firing `$exposure` on
				// that would report a cohort member the experiment service never recorded, so only a `true`
				// marks this petition treated. A `false` is terminal for this petition: the ref above is already
				// set, so there is no retry, and no exposure fires.
				if (await treatExperiment(utilityContext, experiment.experimentName)) {
					setTreatedPetitionId(petitionId);
				}
			} catch (e: unknown) {
				// Backstop if treatExperiment's resolve-never-reject contract ever regresses — this must not be
				// silent, since it would drop $exposure for the whole experiment.
				void utilityContext.errorReporter.report({
					error:
						e instanceof Error
							? e
							: new Error('useTreatLastNameInitialExperiment: unexpected rejection from treatExperiment'),
					params: { experimentName: experiment.experimentName },
				});
			}
		})();
	}, [qualified, isRealAssignment, experiment, utilityContext, petitionId]);

	useEffect(() => {
		if (!resolvedExperiment.treated) return;
		void trackExperimentExposure();
	}, [resolvedExperiment.treated, trackExperimentExposure]);
}
